import Zoom from 'react-medium-image-zoom'
import {
  MarkBadge,
  MarkedImageWrapper,
  MarkOverlay,
  MarksOverlayLayer
} from './ActivityMarkPhoto.styles'
import { PhotoMark } from './PhotoMarkingModal'
import { ReactElement, useCallback, useState } from 'react'
import styled from 'styled-components'
import { useTheme } from 'styled-components'
import { mostReadable } from '@ctrl/tinycolor'

type Props = {
  src: string
  des?: string
  marks: PhotoMark[]
  markColor?: string
}

type ZoomContentProps = React.ComponentProps<
  NonNullable<React.ComponentProps<typeof Zoom>['ZoomContent']>
>

type OnZoomChange = NonNullable<
  React.ComponentProps<typeof Zoom>['onZoomChange']
>

type ImgElement = ReactElement<HTMLImageElement> | null

const Image = styled.img`
  object-fit: cover;
`

export default function MarkedImage({ src, des, marks, markColor }: Props) {
  const theme = useTheme()

  const markOverlayColor = markColor || theme.colors.primary
  const badgeTextColor = mostReadable(markOverlayColor, [
    theme.colors.blackText,
    theme.colors.whiteText
  ])?.toHexString()

  const zoomContent = useCallback(
    ({ img, buttonUnzoom }: ZoomContentProps) => (
      <>
        <div
          style={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center'
          }}
        >
          {img}
          {marks.length > 0 && (
            <MarksOverlayLayer
              data-rmiz-modal-img
              style={(img as ImgElement)?.props.style as React.CSSProperties}
            >
              {marks.map((mark, index) => (
                <MarkOverlay
                  key={index}
                  x={mark.x}
                  y={mark.y}
                  width={mark.width}
                  height={mark.height}
                  color={markOverlayColor}
                >
                  <MarkBadge
                    backgroundColor={markOverlayColor}
                    textColor={badgeTextColor || theme.colors.whiteText}
                  >{`#${index + 1} | ${mark.category.des}`}</MarkBadge>
                </MarkOverlay>
              ))}
            </MarksOverlayLayer>
          )}
        </div>
        {buttonUnzoom}
      </>
    ),
    [marks, markOverlayColor, badgeTextColor, theme.colors.whiteText]
  )

  const [isZoomed, setIsZoomed] = useState(false)

  const handleZoomChange = useCallback<OnZoomChange>((isZooming, { event }) => {
    const hasFinishedZoom = !isZooming && event.type === 'close'
    if (isZooming || hasFinishedZoom) {
      setIsZoomed(isZooming)
    }
  }, [])

  return (
    <MarkedImageWrapper>
      <Zoom onZoomChange={handleZoomChange} ZoomContent={zoomContent}>
        <Image src={src} className='img-fluid' alt={`${des || 'Photo'}`} />
        {!isZoomed && (
          <MarksOverlayLayer>
            {marks.map((mark, index) => (
              <MarkOverlay
                key={index}
                x={mark.x}
                y={mark.y}
                width={mark.width}
                height={mark.height}
                color={markOverlayColor}
              >
                <MarkBadge
                  backgroundColor={markOverlayColor}
                  textColor={badgeTextColor || theme.colors.whiteText}
                >
                  {`#${index + 1} | ${mark.category.des}`}
                </MarkBadge>
              </MarkOverlay>
            ))}
          </MarksOverlayLayer>
        )}
      </Zoom>
    </MarkedImageWrapper>
  )
}
